import { Action, createActions, handleActions } from 'redux-actions';

import { MFAItem, User } from 'core/models/userModel';
import { getStoredAccessToken } from 'utils/localStorage';
import { getTenantData } from 'utils/util';

export interface SessionState {
  vivalinkTheme: string;
  isAuth: boolean;
  user?: User;
  mfa?: MFAItem[];
  currSiteId?: string;
  siteList: Record<string, any>[];
  sensorTypeList: Record<string, any>[];
  dataMap: Record<string, any>;
}

export interface SessionPayload {
  user?: User;
  mfa?: MFAItem[];
  vivalinkTheme?: string;
  currSiteId?: string;
  siteList?: Record<string, any>[];
  sensorTypeList?: Record<string, any>[];
  dataMap?: Record<string, any>;
}

export interface SessionReducer {
  [action: string]: (
    state: SessionState,
    action?: Action<SessionPayload>,
  ) => SessionState;
}

export const defaultState: SessionState = {
  mfa: [],
  vivalinkTheme: localStorage.getItem('vivalinkTheme') || 'light',
  isAuth: !!getStoredAccessToken(),
  siteList: [],
  currSiteId: getTenantData().siteId || '',
  sensorTypeList: [],
  dataMap: {},
};

const prefix = {
  prefix: 'SESSION', // String used to prefix each type
  namespace: '--', // Separator between prefix and type.  Default: `/`
};

export const {
  setVivalinkTheme,
  setAuth,
  setMfa,
  setUser,
  removeUser,
  setSite,
  setCurrSiteId,
  setSensorType,
  setDataMap,
} = createActions<SessionPayload>(
  {
    SET_VIVALINK_THEME: (vivalinkTheme) => vivalinkTheme,
    SET_AUTH: (auth) => auth,
    SET_USER: (user) => user,
    set_MFA: (mfa) => mfa,
    REMOVE_USER: (state) => state,
    SET_SITE: (sites) => sites,
    SET_CURR_SITE_ID: (site) => site,
    SET_SENSOR_TYPE: (sensorType) => sensorType,
    SET_DATA_MAP: (data) => data,
  },
  prefix,
);

const sessionReducer = {
  SET_MFA: (
    state: SessionState,
    { payload: { mfa } }: Action<SessionPayload>,
  ): SessionState => {
    return {
      ...state,
      isAuth: true,
      mfa,
    };
  },
  SET_USER: (
    state: SessionState,
    { payload: { user } }: Action<SessionPayload>,
  ): SessionState => {
    const webVersionInfoUrl = process.env.REACT_APP_VERSION_INFO_URL;
    const webVersionInfoVersion = process.env.REACT_APP_VERSION_INFO_VERSION;
    const webVersionInfoTime = process.env.REACT_APP_VERSION_INFO_TIME;
    if (user != null && user.webVersionInfo != null) {
      if (webVersionInfoUrl != null && webVersionInfoUrl !== '') {
        user.webVersionInfo.url = webVersionInfoUrl;
      }
      if (webVersionInfoVersion != null && webVersionInfoVersion !== '') {
        user.webVersionInfo.version = webVersionInfoVersion;
      }
      if (webVersionInfoTime != null && webVersionInfoTime !== '') {
        user.webVersionInfo.time = webVersionInfoTime;
      }
    }
    return {
      ...state,
      isAuth: true,
      user,
    };
  },
  SET_SITE: (
    state: SessionState,
    { payload: { siteList = [] } }: Action<SessionPayload>,
  ): SessionState => {
    return {
      ...state,
      siteList,
    };
  },
  SET_VIVALINK_THEME: (
    state: SessionState,
    { payload: { vivalinkTheme = 'light' } }: Action<SessionPayload>,
  ): SessionState => {
    return {
      ...state,
      vivalinkTheme,
    };
  },
  SET_CURR_SITE_ID: (
    state: SessionState,
    { payload: { currSiteId = '' } }: Action<SessionPayload>,
  ): SessionState => {
    return {
      ...state,
      currSiteId,
    };
  },
  SET_SENSOR_TYPE: (
    state: SessionState,
    { payload: { sensorTypeList = [] } }: Action<SessionPayload>,
  ): SessionState => {
    return {
      ...state,
      sensorTypeList,
    };
  },
  SET_DATA_MAP: (
    state: SessionState,
    { payload: { dataMap = {} } }: Action<SessionPayload>,
  ): SessionState => {
    return {
      ...state,
      dataMap,
    };
  },

  REMOVE_USER: (state: SessionState): SessionState => ({
    ...state,
    user: undefined,
    isAuth: false,
  }),
} as SessionReducer;

export const session = handleActions<SessionState>(
  sessionReducer,
  defaultState,
  prefix,
);
